import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

// Cache helpers — key gắn theo user.id để 2 tài khoản khác nhau trên
// cùng thiết bị không đọc nhầm dữ liệu cache của nhau
let cacheUid = 'anon';
export function setCacheUserId(uid: string | null) {
  cacheUid = uid || 'anon';
}
const cacheKey = (key: string) => `__cache_${cacheUid}_${key}`;

function loadCache<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire sau 30 phút
    if (Date.now() - (parsed._ts || 0) > 30 * 60 * 1000) return null;
    return parsed.data as T;
  } catch {
    return null;
  }
}
function saveCache<T>(key: string, data: T) {
  try {
    localStorage.setItem(cacheKey(key), JSON.stringify({ data, _ts: Date.now() }));
  } catch {
    // ignore
  }
}

// ─── GLOBAL SYNC BROADCAST ─────────────────────────────────────────────────
// Đơn giản: khi bất kỳ store nào thay đổi data, nó gọi broadcastSync()
// Tất cả các store khác sẽ tự động refetch.
const SYNC_CHANNEL = '__store_sync__';
type StoreKey = 'customers' | 'loans' | 'inventory' | 'revenue' | 'all';
const syncListeners: Map<StoreKey, Set<() => void>> = new Map();

function getListeners(key: StoreKey): Set<() => void> {
  if (!syncListeners.has(key)) syncListeners.set(key, new Set());
  return syncListeners.get(key)!;
}

export function subscribeSync(key: StoreKey, fn: () => void) {
  getListeners(key).add(fn);
  return () => getListeners(key).delete(fn);
}

export function broadcastSync(key?: StoreKey) {
  // Báo cho store tương ứng
  if (key) getListeners(key).forEach((fn) => fn());
  // Và báo cho tất cả (all)
  getListeners('all').forEach((fn) => fn());
  // Cũng báo cho legacy customer sync
  customerSyncListeners.forEach((fn) => fn());
}

// ─── REALTIME SYNC (đồng bộ đa thiết bị cùng tiệm) ──────────────────────
// 1 channel chung lắng nghe postgres_changes trên mọi bảng nghiệp vụ.
// RLS tự lọc theo shop_id → mỗi máy chỉ nhận event của tiệm mình.
// Có thay đổi → broadcastSync() → các store refetch.
let realtimeChannel: ReturnType<typeof supabase.channel> | null = null;

const REALTIME_TABLES = [
  'customers', 'loans', 'inventory', 'revenue_logs', 'loan_history',
  'confiscations', 'vehicle_prices', 'customer_images',
];
const SHOP_TABLES = new Set(['shops', 'capital_logs']);

export function startRealtimeSync() {
  if (realtimeChannel) return;
  const ch = supabase.channel('store-sync');
  for (const t of [...REALTIME_TABLES, ...SHOP_TABLES]) {
    ch.on('postgres_changes', { event: '*', schema: 'public', table: t }, () => {
      broadcastSync();
      // Bảng shops/capital → các hook useShop/useCapital cũng refetch
      if (SHOP_TABLES.has(t)) window.dispatchEvent(new Event('shop-updated'));
    });
  }
  ch.subscribe();
  realtimeChannel = ch;
}

export function stopRealtimeSync() {
  if (!realtimeChannel) return;
  supabase.removeChannel(realtimeChannel);
  realtimeChannel = null;
}

// ─── CUSTOMER SYNC SIGNAL ─────────────────────────────────────────────────
type SyncListener = () => void;
const customerSyncListeners: Set<SyncListener> = new Set();

// ─── TYPES ────────────────────────────────────────────────────────────────
export interface Customer {
  id: string;
  name: string;
  phone: string;
  idCard: string;
  address: string;
  createdAt: string;
  totalLoans: number;
  activeLoans: number;
  totalBorrowed: number;
}

export interface Loan {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  assetName: string;
  assetCategory: string;
  loanAmount: number;
  interestRate: number;
  startDate: string;
  dueDate: string;
  status: 'active' | 'overdue' | 'due_soon' | 'redeemed' | 'forfeited' | 'interest_paid';
  warehouse: string;
}

export interface InventoryItem {
  id: string;
  name: string;
  category: string;
  description: string;
  estimatedValue: number;
  loanAmount: number;
  status: 'pawned' | 'redeemed' | 'forfeited' | 'available';
  loanId: string | null;
  customerName: string | null;
  receivedDate: string;
  condition: 'excellent' | 'good' | 'fair' | 'poor';
  warehouse: string;
}

export interface LoanHistory {
  id: string;
  loanId: string;
  fieldName: string;
  oldValue: string;
  newValue: string;
  changedBy: string;
  changedAt: string;
}

function mapLoanHistory(row: Record<string, unknown>): LoanHistory {
  return {
    id: row.id as string,
    loanId: row.loan_id as string,
    fieldName: row.field_name as string,
    oldValue: (row.old_value as string) || '',
    newValue: (row.new_value as string) || '',
    changedBy: (row.changed_by as string) || 'Hệ thống',
    changedAt: row.changed_at as string,
  };
}

const fieldLabelMap: Record<string, string> = {
  status: 'Trạng thái',
  due_date: 'Hạn trả',
  loan_amount: 'Giá trị vay',
  interest_rate: 'Lãi suất',
  start_date: 'Ngày vay',
  customer_name: 'Khách hàng',
  customer_phone: 'Số điện thoại',
  asset_name: 'Tài sản',
  asset_category: 'Danh mục',
  warehouse: 'Kho xe',
};

export function useLoanHistoryStore() {
  const [history, setHistory] = useState<LoanHistory[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async (loanId: string) => {
    setLoading(true);
    const { data } = await supabase
      .from('loan_history')
      .select('*')
      .eq('loan_id', loanId)
      .order('changed_at', { ascending: false });
    if (data) setHistory(data.map(mapLoanHistory));
    setLoading(false);
  }, []);

  const addHistory = async (loanId: string, fieldName: string, oldValue: string, newValue: string, changedBy: string) => {
    const id = `LH${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const { data, error } = await supabase.from('loan_history').insert({
      id,
      loan_id: loanId,
      field_name: fieldName,
      old_value: oldValue,
      new_value: newValue,
      changed_by: changedBy,
      changed_at: new Date().toISOString(),
    }).select().maybeSingle();
    if (error) console.error('Lỗi ghi lịch sử:', error.message);
    if (data) setHistory((prev) => [mapLoanHistory(data as Record<string, unknown>), ...prev]);
  };

  return { history, loading, fetchHistory, addHistory };
}

export { fieldLabelMap };

// ─── CUSTOMERS ─────────────────────────────────────────────────────────────
function mapCustomer(row: Record<string, unknown>): Customer {
  return {
    id: row.id as string,
    name: row.name as string,
    phone: row.phone as string,
    idCard: row.id_card as string,
    address: (row.address as string) || '',
    createdAt: row.created_at as string,
    totalLoans: Number(row.total_loans || 0),
    activeLoans: Number(row.active_loans || 0),
    totalBorrowed: Number(row.total_borrowed || 0),
  };
}

export function useCustomersStore() {
  const [customers, setCustomers] = useState<Customer[]>(() => loadCache<Customer[]>('customers') ?? []);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  const fetchCustomers = useCallback(async () => {
    const { data } = await supabase.from('customers').select('*').order('created_at', { ascending: false });
    if (data && isMounted.current) {
      const mapped = data.map(mapCustomer);
      setCustomers(mapped);
      saveCache('customers', mapped);
    }
  }, []);

  const fetchCustomersWithLoading = useCallback(async () => {
    const cached = loadCache<Customer[]>('customers');
    if (!cached) setLoading(true);
    await fetchCustomers();
    if (isMounted.current) setLoading(false);
  }, [fetchCustomers]);

  useEffect(() => {
    isMounted.current = true;
    fetchCustomersWithLoading();
    const unsub1 = subscribeSync('customers', fetchCustomers);
    const unsub2 = subscribeSync('all', fetchCustomers);
    return () => {
      isMounted.current = false;
      unsub1();
      unsub2();
    };
  }, [fetchCustomersWithLoading, fetchCustomers]);

  const addCustomer = async (c: Customer) => {
    const { data, error } = await supabase.from('customers').insert({
      id: c.id, name: c.name, phone: c.phone, id_card: c.idCard,
      address: c.address, created_at: c.createdAt,
      total_loans: c.totalLoans, active_loans: c.activeLoans, total_borrowed: c.totalBorrowed,
    }).select().maybeSingle();
    if (error) throw new Error(error.message);
    if (data) {
      const mapped = mapCustomer(data as Record<string, unknown>);
      setCustomers((prev) => [mapped, ...prev]);
      saveCache('customers', [mapped, ...customers]);
    }
    broadcastSync('customers');
  };

  const updateCustomer = async (id: string, updates: Partial<Customer>) => {
    const dbUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
    if (updates.idCard !== undefined) dbUpdates.id_card = updates.idCard;
    if (updates.address !== undefined) dbUpdates.address = updates.address;
    if (updates.totalLoans !== undefined) dbUpdates.total_loans = updates.totalLoans;
    if (updates.activeLoans !== undefined) dbUpdates.active_loans = updates.activeLoans;
    if (updates.totalBorrowed !== undefined) dbUpdates.total_borrowed = updates.totalBorrowed;
    await supabase.from('customers').update(dbUpdates).eq('id', id);
    const updated = customers.map((c) => (c.id === id ? { ...c, ...updates } : c));
    setCustomers(updated);
    saveCache('customers', updated);
    broadcastSync('customers');
  };

  const deleteCustomer = async (id: string) => {
    const { data: customerLoans } = await supabase.from('loans').select('id').eq('customer_id', id);
    if (customerLoans && customerLoans.length > 0) {
      const loanIds = customerLoans.map((l: Record<string, unknown>) => l.id as string);
      await supabase.from('revenue_logs').delete().in('loan_id', loanIds);
      await supabase.from('inventory').delete().in('loan_id', loanIds);
      await supabase.from('loans').delete().eq('customer_id', id);
    }
    await supabase.from('customers').delete().eq('id', id);
    const filtered = customers.filter((c) => c.id !== id);
    setCustomers(filtered);
    saveCache('customers', filtered);
    broadcastSync('all');
  };

  const refreshCustomerStats = async (customerId: string) => {
    await syncCustomerStatsFromLoans(customerId);
  };

  return { customers, loading, addCustomer, updateCustomer, deleteCustomer, refreshCustomerStats, refetch: fetchCustomersWithLoading };
}

// ─── HELPER: Sync customer stats from loans ─────────────────────────────────
export async function syncCustomerStatsFromLoans(customerId: string): Promise<{
  activeLoans: number;
  totalBorrowed: number;
  totalLoans: number;
} | null> {
  const { data: customerLoans } = await supabase
    .from('loans')
    .select('loan_amount, status')
    .eq('customer_id', customerId);

  if (!customerLoans) return null;

  const activeStatuses = ['active', 'overdue', 'due_soon'];
  const activeLoansCount = customerLoans.filter(
    (l: Record<string, unknown>) => activeStatuses.includes(l.status as string)
  ).length;
  const totalBorrowed = customerLoans
    .filter((l: Record<string, unknown>) => activeStatuses.includes(l.status as string))
    .reduce((sum: number, l: Record<string, unknown>) => sum + (l.loan_amount as number), 0);
  const totalLoans = customerLoans.length;

  await supabase.from('customers').update({
    active_loans: activeLoansCount,
    total_borrowed: totalBorrowed,
    total_loans: totalLoans,
  }).eq('id', customerId);

  broadcastSync('customers');

  return { activeLoans: activeLoansCount, totalBorrowed, totalLoans };
}

// ─── HELPER: Fix all sync issues across the database ──────────────────────
export async function fixSyncIssues(): Promise<{
  fixedLoans: number;
  fixedCustomers: number;
  errors: string[];
}> {
  const errors: string[] = [];
  let fixedLoans = 0;
  let fixedCustomers = 0;

  const invStatusMap: Record<string, string> = {
    active: 'pawned', overdue: 'pawned', due_soon: 'pawned', interest_paid: 'pawned',
    redeemed: 'redeemed', forfeited: 'forfeited',
  };

  // 1. Fix inventory status & loan_amount for every loan
  const { data: allLoans } = await supabase.from('loans').select('id, status, loan_amount, customer_id');
  if (!allLoans) {
    return { fixedLoans: 0, fixedCustomers: 0, errors: ['Không thể tải danh sách khoản vay'] };
  }

  for (const loan of allLoans) {
    const expectedInvStatus = invStatusMap[loan.status as string];
    const { error: invError } = await supabase
      .from('inventory')
      .update({ status: expectedInvStatus, loan_amount: loan.loan_amount })
      .eq('loan_id', loan.id as string);
    if (invError) {
      errors.push(`KV ${loan.id}: ${invError.message}`);
    } else {
      fixedLoans++;
    }
  }

  // 2. Recalculate all customer stats
  const { data: allCustomers } = await supabase.from('customers').select('id');
  if (allCustomers) {
    for (const c of allCustomers) {
      try {
        const result = await syncCustomerStatsFromLoans(c.id as string);
        if (result) fixedCustomers++;
      } catch (e) {
        errors.push(`KH ${c.id}: ${(e as Error).message}`);
      }
    }
  }

  broadcastSync('all');
  return { fixedLoans, fixedCustomers, errors };
}

// ─── LOANS ─────────────────────────────────────────────────────────────────
function mapLoan(row: Record<string, unknown>): Loan {
  return {
    id: row.id as string,
    customerId: row.customer_id as string,
    customerName: row.customer_name as string,
    customerPhone: row.customer_phone as string,
    assetName: row.asset_name as string,
    assetCategory: row.asset_category as string,
    loanAmount: Number(row.loan_amount || 0),
    interestRate: Number(row.interest_rate || 0),
    startDate: row.start_date as string,
    dueDate: row.due_date as string,
    status: row.status as Loan['status'],
    warehouse: (row.warehouse as string) || '',
  };
}

export function useLoansStore() {
  const [loans, setLoans] = useState<Loan[]>(() => loadCache<Loan[]>('loans') ?? []);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('loans').select('*').order('start_date', { ascending: false });
    if (data && isMounted.current) {
      const mapped = data.map(mapLoan);
      setLoans(mapped);
      saveCache('loans', mapped);
    }
    if (isMounted.current) setLoading(false);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    if (!loadCache<Loan[]>('loans')) setLoading(true);
    fetchLoans();
    const unsub1 = subscribeSync('loans', fetchLoans);
    const unsub2 = subscribeSync('all', fetchLoans);
    return () => {
      isMounted.current = false;
      unsub1();
      unsub2();
    };
  }, [fetchLoans]);

  const addLoan = async (loan: Loan) => {
    const { data, error } = await supabase.from('loans').insert({
      id: loan.id, customer_id: loan.customerId,
      customer_name: loan.customerName, customer_phone: loan.customerPhone,
      asset_name: loan.assetName, asset_category: loan.assetCategory,
      loan_amount: loan.loanAmount, interest_rate: loan.interestRate,
      start_date: loan.startDate, due_date: loan.dueDate, status: loan.status,
      warehouse: loan.warehouse || '',
    }).select().maybeSingle();
    if (error) throw new Error(error.message);
    if (data) {
      const mapped = mapLoan(data as Record<string, unknown>);
      setLoans((prev) => [mapped, ...prev]);
      saveCache('loans', [mapped, ...loans]);
    }
    if (loan.customerId) await syncCustomerStatsFromLoans(loan.customerId);
    broadcastSync('loans');
  };

  const updateLoan = async (id: string, updates: Partial<Loan>) => {
    const existingLoan = loans.find((l) => l.id === id);
    const customerId = existingLoan?.customerId;

    // Get current user email for history
    const { data: userData } = await supabase.auth.getUser();
    const changedBy = userData.user?.email || 'Hệ thống';

    // Track history for changed fields
    const historyEntries: { field: string; oldVal: string; newVal: string }[] = [];

    const dbUpdates: Record<string, unknown> = {};
    if (updates.status !== undefined && updates.status !== existingLoan?.status) {
      dbUpdates.status = updates.status;
      historyEntries.push({ field: 'status', oldVal: existingLoan?.status || '', newVal: updates.status });
    }
    if (updates.dueDate !== undefined && updates.dueDate !== existingLoan?.dueDate) {
      dbUpdates.due_date = updates.dueDate;
      historyEntries.push({ field: 'due_date', oldVal: existingLoan?.dueDate || '', newVal: updates.dueDate });
    }
    if (updates.loanAmount !== undefined && updates.loanAmount !== existingLoan?.loanAmount) {
      dbUpdates.loan_amount = updates.loanAmount;
      historyEntries.push({ field: 'loan_amount', oldVal: String(existingLoan?.loanAmount || 0), newVal: String(updates.loanAmount) });
    }
    if (updates.interestRate !== undefined && updates.interestRate !== existingLoan?.interestRate) {
      dbUpdates.interest_rate = updates.interestRate;
      historyEntries.push({ field: 'interest_rate', oldVal: String(existingLoan?.interestRate || 0), newVal: String(updates.interestRate) });
    }
    if (updates.startDate !== undefined && updates.startDate !== existingLoan?.startDate) {
      dbUpdates.start_date = updates.startDate;
      historyEntries.push({ field: 'start_date', oldVal: existingLoan?.startDate || '', newVal: updates.startDate });
    }
    if (updates.customerName !== undefined && updates.customerName !== existingLoan?.customerName) {
      dbUpdates.customer_name = updates.customerName;
      historyEntries.push({ field: 'customer_name', oldVal: existingLoan?.customerName || '', newVal: updates.customerName });
    }
    if (updates.customerPhone !== undefined && updates.customerPhone !== existingLoan?.customerPhone) {
      dbUpdates.customer_phone = updates.customerPhone;
      historyEntries.push({ field: 'customer_phone', oldVal: existingLoan?.customerPhone || '', newVal: updates.customerPhone });
    }
    if (updates.assetName !== undefined && updates.assetName !== existingLoan?.assetName) {
      dbUpdates.asset_name = updates.assetName;
      historyEntries.push({ field: 'asset_name', oldVal: existingLoan?.assetName || '', newVal: updates.assetName });
    }
    if (updates.assetCategory !== undefined && updates.assetCategory !== existingLoan?.assetCategory) {
      dbUpdates.asset_category = updates.assetCategory;
      historyEntries.push({ field: 'asset_category', oldVal: existingLoan?.assetCategory || '', newVal: updates.assetCategory });
    }
    if (updates.warehouse !== undefined && updates.warehouse !== existingLoan?.warehouse) {
      dbUpdates.warehouse = updates.warehouse;
      historyEntries.push({ field: 'warehouse', oldVal: existingLoan?.warehouse || '', newVal: updates.warehouse });
    }

    if (Object.keys(dbUpdates).length > 0) {
      await supabase.from('loans').update(dbUpdates).eq('id', id);
      const updated = loans.map((l) => (l.id === id ? { ...l, ...updates } : l));
      setLoans(updated);
      saveCache('loans', updated);

      // ── ĐỒNG BỘ TỒN KHO KHI TRẠNG THÁI / GIÁ TRỊ THAY ĐỔI ──
      const invStatusMap: Record<string, string> = {
        active: 'pawned', overdue: 'pawned', due_soon: 'pawned', interest_paid: 'pawned',
        redeemed: 'redeemed', forfeited: 'forfeited',
      };
      if (updates.status !== undefined && invStatusMap[updates.status]) {
        await supabase.from('inventory').update({ status: invStatusMap[updates.status] }).eq('loan_id', id);
        broadcastSync('inventory');
      }
      if (updates.loanAmount !== undefined) {
        await supabase.from('inventory').update({ loan_amount: updates.loanAmount }).eq('loan_id', id);
        broadcastSync('inventory');
      }

      // Write history entries
      for (const entry of historyEntries) {
        const histId = `LH${Date.now()}_${Math.random().toString(36).slice(2, 6)}_${entry.field}`;
        await supabase.from('loan_history').insert({
          id: histId,
          loan_id: id,
          field_name: entry.field,
          old_value: entry.oldVal,
          new_value: entry.newVal,
          changed_by: changedBy,
          changed_at: new Date().toISOString(),
        });
      }
    }

    if (customerId && (updates.customerName !== undefined || updates.customerPhone !== undefined)) {
      const customerUpdates: Record<string, unknown> = {};
      if (updates.customerName !== undefined) customerUpdates.name = updates.customerName;
      if (updates.customerPhone !== undefined) customerUpdates.phone = updates.customerPhone;
      await supabase.from('customers').update(customerUpdates).eq('id', customerId);
    }

    // Tính lại thống kê khách hàng khi status hoặc loanAmount thay đổi
    if (customerId && (updates.status !== undefined || updates.loanAmount !== undefined)) {
      await syncCustomerStatsFromLoans(customerId);
    }
    broadcastSync('loans');
  };

  const deleteLoan = async (id: string) => {
    const existingLoan = loans.find((l) => l.id === id);
    const customerId = existingLoan?.customerId;
    await supabase.from('inventory').delete().eq('loan_id', id);
    await supabase.from('revenue_logs').delete().eq('loan_id', id);
    await supabase.from('loans').delete().eq('id', id);
    const filtered = loans.filter((l) => l.id !== id);
    setLoans(filtered);
    saveCache('loans', filtered);
    if (customerId) await syncCustomerStatsFromLoans(customerId);
    broadcastSync('all');
  };

  return { loans, loading, addLoan, updateLoan, deleteLoan, refetch: fetchLoans };
}

// ─── REVENUE LOGS ──────────────────────────────────────────────────────────
export interface RevenueLog {
  id: string;
  loanId: string;
  customerName: string;
  assetName: string;
  type: 'extend' | 'redeem' | 'other';
  interestAmount: number;
  extendInterest: number;
  oldInterest: number;
  totalPayback: number;
  extendDays: number;
  note: string;
  createdAt: string;
}

function mapRevenueLog(row: Record<string, unknown>): RevenueLog {
  return {
    id: row.id as string,
    loanId: row.loan_id as string,
    customerName: row.customer_name as string,
    assetName: row.asset_name as string,
    type: (row.type as RevenueLog['type']) || 'extend',
    interestAmount: Number(row.interest_amount || 0),
    extendInterest: Number(row.extend_interest || 0),
    oldInterest: Number(row.old_interest || 0),
    totalPayback: Number(row.total_payback || 0),
    extendDays: Number(row.extend_days || 0),
    note: (row.note as string) || '',
    createdAt: row.created_at as string,
  };
}

export function useRevenueStore() {
  const [logs, setLogs] = useState<RevenueLog[]>(() => loadCache<RevenueLog[]>('revenue_logs') ?? []);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  const fetchLogs = useCallback(async () => {
    const { data } = await supabase
      .from('revenue_logs')
      .select('*')
      .order('created_at', { ascending: false });
    if (data && isMounted.current) {
      const mapped = data.map(mapRevenueLog);
      setLogs(mapped);
      saveCache('revenue_logs', mapped);
    }
  }, []);

  const fetchLogsWithLoading = useCallback(async () => {
    const cached = loadCache<RevenueLog[]>('revenue_logs');
    if (!cached) setLoading(true);
    await fetchLogs();
    if (isMounted.current) setLoading(false);
  }, [fetchLogs]);

  useEffect(() => {
    isMounted.current = true;
    fetchLogsWithLoading();
    const unsub1 = subscribeSync('revenue', fetchLogs);
    const unsub2 = subscribeSync('all', fetchLogs);
    return () => {
      isMounted.current = false;
      unsub1();
      unsub2();
    };
  }, [fetchLogsWithLoading, fetchLogs]);

  const addRevenueLog = async (log: RevenueLog) => {
    const { data, error } = await supabase.from('revenue_logs').insert({
      id: log.id,
      loan_id: log.loanId,
      customer_name: log.customerName,
      asset_name: log.assetName,
      type: log.type,
      interest_amount: log.interestAmount,
      extend_interest: log.extendInterest,
      old_interest: log.oldInterest,
      total_payback: log.totalPayback,
      extend_days: log.extendDays,
      note: log.note,
      created_at: new Date().toISOString(),
    }).select().maybeSingle();
    if (error) throw new Error(error.message);
    if (data) {
      const mapped = mapRevenueLog(data as Record<string, unknown>);
      setLogs((prev) => [mapped, ...prev]);
      saveCache('revenue_logs', [mapped, ...logs]);
    }
    broadcastSync('revenue');
  };

  const getMonthRevenue = (yearMonth: string) =>
    logs
      .filter((l) => l.createdAt.startsWith(yearMonth))
      .reduce((sum, l) => sum + l.interestAmount, 0);

  const currentMonthRevenue = (() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return getMonthRevenue(ym);
  })();

  return { logs, loading, addRevenueLog, getMonthRevenue, currentMonthRevenue, refetch: fetchLogsWithLoading };
}

// ─── INVENTORY ─────────────────────────────────────────────────────────────
function mapInventory(row: Record<string, unknown>): InventoryItem {
  return {
    id: row.id as string,
    name: row.name as string,
    category: row.category as string,
    description: (row.description as string) || '',
    estimatedValue: Number(row.estimated_value || 0),
    loanAmount: Number(row.loan_amount || 0),
    status: row.status as InventoryItem['status'],
    loanId: (row.loan_id as string) || null,
    customerName: (row.customer_name as string) || null,
    receivedDate: row.received_date as string,
    condition: row.condition as InventoryItem['condition'],
    warehouse: (row.warehouse as string) || '',
  };
}

export function useInventoryStore() {
  const [items, setItems] = useState<InventoryItem[]>(() => loadCache<InventoryItem[]>('inventory') ?? []);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  const fetchItems = useCallback(async () => {
    const { data } = await supabase.from('inventory').select('*').order('received_date', { ascending: false });
    if (data && isMounted.current) {
      const mapped = data.map(mapInventory);
      setItems(mapped);
      saveCache('inventory', mapped);
    }
  }, []);

  const fetchItemsWithLoading = useCallback(async () => {
    const cached = loadCache<InventoryItem[]>('inventory');
    if (!cached) setLoading(true);
    await fetchItems();
    if (isMounted.current) setLoading(false);
  }, [fetchItems]);

  useEffect(() => {
    isMounted.current = true;
    fetchItemsWithLoading();
    const unsub1 = subscribeSync('inventory', fetchItems);
    const unsub2 = subscribeSync('all', fetchItems);
    return () => {
      isMounted.current = false;
      unsub1();
      unsub2();
    };
  }, [fetchItemsWithLoading, fetchItems]);

  const addItem = async (item: InventoryItem) => {
    const { data, error } = await supabase.from('inventory').insert({
      id: item.id, name: item.name, category: item.category,
      description: item.description, estimated_value: item.estimatedValue,
      loan_amount: item.loanAmount, status: item.status,
      loan_id: item.loanId, customer_name: item.customerName,
      received_date: item.receivedDate, condition: item.condition,
      warehouse: item.warehouse || '',
    }).select().maybeSingle();
    if (error) throw new Error(error.message);
    if (data) {
      const mapped = mapInventory(data as Record<string, unknown>);
      setItems((prev) => [mapped, ...prev]);
      saveCache('inventory', [mapped, ...items]);
    }
    broadcastSync('inventory');
  };

  const updateItem = async (id: string, updates: Partial<InventoryItem>) => {
    const dbUpdates: Record<string, unknown> = {};
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.condition !== undefined) dbUpdates.condition = updates.condition;
    if (updates.estimatedValue !== undefined) dbUpdates.estimated_value = updates.estimatedValue;
    if (updates.loanAmount !== undefined) dbUpdates.loan_amount = updates.loanAmount;
    if (updates.customerName !== undefined) dbUpdates.customer_name = updates.customerName;
    if (updates.loanId !== undefined) dbUpdates.loan_id = updates.loanId;
    await supabase.from('inventory').update(dbUpdates).eq('id', id);
    const updated = items.map((i) => (i.id === id ? { ...i, ...updates } : i));
    setItems(updated);
    saveCache('inventory', updated);
    broadcastSync('inventory');
  };

  const deleteItem = async (id: string) => {
    const item = items.find((i) => i.id === id);
    if (item?.loanId) {
      await supabase.from('revenue_logs').delete().eq('loan_id', item.loanId);
    }
    await supabase.from('inventory').delete().eq('id', id);
    const filtered = items.filter((i) => i.id !== id);
    setItems(filtered);
    saveCache('inventory', filtered);
    broadcastSync('all');
  };

  return { items, loading, addItem, updateItem, deleteItem, refetch: fetchItemsWithLoading };
}
